import get from "lodash/get";
import { debounce } from "lodash";
import { useEffect, useMemo } from "react";

import {
  Dealer,
  GetMyDealersQueryVariables,
  useGetMyDealersLazyQuery,
} from "~/schemas/generated/graphql";

type TUseGetAllDealersForSelectOption = {
  customHandler?: (dealer: Dealer) => { label: string; value: string };
  searchDebounceMs?: number;
  variables?: GetMyDealersQueryVariables;
};

export const useGetAllDealersForSelect = (
  options?: TUseGetAllDealersForSelectOption,
) => {
  const [getMyDealersQuery, { data, loading, fetchMore }] =
    useGetMyDealersLazyQuery({
      variables: {
        page: 0,
        partName: null,
        count: 25,
        ...(options ?? { variables: {} }).variables,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    getMyDealersQuery();
  }, []);

  const dealers = useMemo(
    () =>
      get(data, "DealerGetter.getMyDealers.items", []).map((dealer) => {
        if (options?.customHandler) return options.customHandler(dealer);
        return {
          label: dealer.DealerName,
          value: dealer.id,
        };
      }),
    [data, options],
  );

  const onScrollBottomDealers = () => {
    const page = Math.ceil(dealers.length / 25);

    fetchMore({
      variables: {
        page,
        count: 25,
      },
      updateQuery: ({ DealerGetter }, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return { DealerGetter };
        }
        return {
          DealerGetter: {
            ...DealerGetter,
            getMyDealers: {
              ...DealerGetter.getMyDealers,
              items: [
                ...DealerGetter.getMyDealers.items,
                ...get(fetchMoreResult, "DealerGetter.getMyDealers.items", []),
              ],
            },
          },
        };
      },
    });
  };

  const searchDealer = debounce((partName: string | null = null) => {
    getMyDealersQuery({
      variables: {
        page: 0,
        partName: partName || null,
      },
    });
  }, options?.searchDebounceMs);

  return {
    dealers,
    searchDealer,
    onScrollBottomDealers,
    loading,
  };
};
